/**
 * LiveDebugPanel – always-on right sidebar that interleaves a thread's chat messages with its
 * tool audit rows, oldest first (newest at the bottom, like the chat). The newest entry is open; older ones fold away as new ones
 * arrive unless the user opened or closed them by hand.
 *
 * It mounts into the app shell's `#theo-live-debug-slot` so it pushes the page over instead of
 * covering the chat. Without that slot (embeds) it falls back to a fixed overlay.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import {
  chatApiInstance as chatApi,
  type ThreadAutomation,
  type ThreadResolverState,
} from "../apis/chatApi";
import {
  getThreadUsage,
  type ThreadUsage,
  type ThreadTurnUsage,
} from "../observer/apis/observerApi";
import { STRUCTURED_VIEWS, StructuredValue, readStoredView, storeView, type StructuredView } from "./StructuredValue";
import { PlanInput, PlanOutput, WriteApiView, isPlanAndRun, isWriteApi, planAndRunSummary, writeApiSummary } from "./PlanAndRunView";
import {
  formatDuration,
  getActionTypeLabel,
  isErrorRow,
  scrollSx,
  type ToolAuditLogRow,
} from "../utils/toolAuditLogUtils";

export const LIVE_DEBUG_SLOT_ID = "theo-live-debug-slot";
const PANEL_WIDTH = 440;
const POLL_WHILE_THINKING_MS = 2500;
const POLL_IDLE_MS = 15000;
// LangSmith ingests runs in batches, so a turn's totals settle a few seconds after it ends.
const LANGSMITH_SETTLE_MS = 8000;

type AuditRow = ToolAuditLogRow & {
  id: number | string;
  audit_event_type?: "playbook_context";
  audit_turn_id?: string | null;
  call_id?: string | null;
  is_undoable?: boolean;
  description?: string;
};

/** A progress line Cortex streamed mid-turn ("Searching users…"), stamped when it arrived. */
export type ProgressUpdate = { key: string; at: number; text: string };

export type LiveDebugEntry =
  | { kind: "message"; key: string; at: number; message: any }
  | { kind: "tool"; key: string; at: number; row: AuditRow }
  | { kind: "turn"; key: string; at: number; turn: ThreadTurnUsage }
  | { kind: "progress"; key: string; at: number; text: string };

function messageTimeMs(message: any): number {
  const created = message?.created_at;
  if (created && typeof created === "object" && created.seconds != null) {
    return Number(created.seconds) * 1000 + Math.floor(Number(created.nanos || 0) / 1e6);
  }
  if (typeof created === "string" || typeof created === "number") {
    const parsed = new Date(created).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  // Pending or streaming messages have no server time yet; they are the newest thing on screen.
  return Number.MAX_SAFE_INTEGER;
}

function auditTimeMs(row: AuditRow): number {
  return isoTimeMs(row.start_timestamp || row.end_timestamp);
}

export function formatTokens(n: number | null | undefined): string {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
}

/** "120.4k · 75%" — cached input tokens and their share of all input tokens. */
export function formatCached(cached: number | null | undefined, input: number | null | undefined): string {
  if (cached == null) return "—";
  return input ? `${formatTokens(cached)} · ${Math.round((100 * cached) / input)}%` : formatTokens(cached);
}

export function formatCost(n: number | null | undefined): string {
  if (n == null) return "—";
  const v = Number(n);
  if (v === 0) return "$0";
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

const AUTOMATION_LABEL: Record<number, string> = { 0: "plain chat", 1: "suggestions", 3: "auto resolver" };
const INTERACTION_LABEL: Record<number, string> = { 0: "no customer contact", 1: "investigative", 2: "full interaction" };

function isoTimeMs(iso: string | null | undefined): number {
  const parsed = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Messages, audit rows, LLM turns and progress updates on one timeline, oldest first. */
const asObject = (value: unknown): any => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/** A chat message that put a card in front of the technician: tool approval or a question/pause. */
export function isHitlCard(message: any): boolean {
  const review = message?.human_review;
  return (
    (Array.isArray(review?.tools) && review.tools.length > 0) ||
    (Array.isArray(review?.information_request) && review.information_request.length > 0)
  );
}

export interface DebugCounts {
  planAndRuns: number;
  /** Replans inside those plan_and_run calls (each result's `replans`). */
  replans: number;
  /** Platform API calls: the steps plan_and_run ran (`calls`) plus the writes write_api sent. */
  apiCalls: number;
  /** Of those, the ones that failed: a FIND that errored, an HTTP status of 400 or more, or the write that failed. */
  apiFailed: number;
  hitlCards: number;
  /** Writes write_api sent to the platform; not ones a retry carried over or the record already showed. */
  writeCalls: number;
  /** Distinct `METHOD path` among those writes. */
  writeEndpoints: number;
}

const PLAN_AND_RUN_TOOL = /_plan_and_run$/;
const WRITE_TOOL = /_write_api$/;

function callFailed(status: unknown): boolean {
  if (status === "error") return true;
  const code = Number(status);
  return typeof status !== "boolean" && status != null && status !== "" && Number.isFinite(code) && code >= 400;
}

/** Thread totals for the header, from the tool audit rows and the chat's cards. */
export function debugCounts(messages: any[], auditRows: ToolAuditLogRow[]): DebugCounts {
  let planAndRuns = 0;
  let replans = 0;
  let apiCalls = 0;
  let apiFailed = 0;
  let writeCalls = 0;
  const endpoints = new Set<string>();
  auditRows.forEach((row) => {
    const name = row.tool_name || "";
    const output = asObject(row.output_json);
    if (PLAN_AND_RUN_TOOL.test(name)) {
      planAndRuns += 1;
      replans += Number(output?.replans) || 0;
      // The HTTP trace, when the row has one, counts every request actually sent (a FIND that paged, a name
      // lookup); older rows only have the runner's one entry per step.
      const http: any[] | null = Array.isArray(output?.debug_trace?.http) ? output.debug_trace.http : null;
      if (http) {
        const sent = http.filter((call) => call?.sent !== false);
        apiCalls += sent.length;
        apiFailed += sent.filter((call) => call?.ok === false).length;
      } else {
        const calls: any[] = Array.isArray(output?.calls) ? output.calls : [];
        apiCalls += calls.length;
        apiFailed += calls.filter((call) => callFailed(call?.status)).length;
      }
    }
    if (WRITE_TOOL.test(name) && output && typeof output.done === "object" && !Array.isArray(output.done)) {
      // runner.send_writes: `writes` in the arguments keyed "1", "2", …; `done` maps each key it reached to its result;
      // `failed` is a key, sent only when the result carries an HTTP status.
      const args = asObject(row.param_json);
      const writes = args && typeof args.writes === "object" && args.writes ? args.writes : {};
      const sent = Object.entries(output.done as Record<string, any>)
        .filter(([, d]) => d && typeof d === "object" && !d.already_done && !("skipped" in d))
        .map(([key]) => key);
      const failed = typeof output.failed === "string" && output.status != null ? output.failed : null;
      if (failed) sent.push(failed);
      sent.forEach((key) => {
        const write = writes[key];
        writeCalls += 1;
        apiCalls += 1;
        if (write?.method) endpoints.add(`${String(write.method).toUpperCase()} ${write.path ?? ""}`);
      });
      if (failed) apiFailed += 1;
    }
  });
  return {
    planAndRuns,
    replans,
    apiCalls,
    apiFailed,
    hitlCards: messages.filter(isHitlCard).length,
    writeCalls,
    writeEndpoints: endpoints.size,
  };
}

export function buildLiveDebugTimeline(
  messages: any[],
  auditRows: AuditRow[],
  turns: ThreadTurnUsage[] = [],
  progress: ProgressUpdate[] = []
): LiveDebugEntry[] {
  const entries: LiveDebugEntry[] = [];
  // Turns go first so a turn marker sorts ahead of anything that started in the same instant.
  turns.forEach((turn) => {
    entries.push({ kind: "turn", key: `turn-${turn.turn_id}`, at: isoTimeMs(turn.start_time), turn });
  });
  messages.forEach((message, index) => {
    entries.push({
      kind: "message",
      key: `msg-${message?.message_id || message?.requestId || `idx-${index}`}`,
      at: messageTimeMs(message),
      message,
    });
  });
  auditRows.forEach((row) => {
    entries.push({ kind: "tool", key: `tool-${row.id}`, at: auditTimeMs(row), row });
  });
  progress.forEach((update) => {
    entries.push({ kind: "progress", key: update.key, at: update.at, text: update.text });
  });
  // Stable on ties: within the same instant keep source order.
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => a.entry.at - b.entry.at || a.index - b.index)
    .map(({ entry }) => entry);
}

function formatClock(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0 || ms === Number.MAX_SAFE_INTEGER) return "now";
  return new Date(ms).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const COLORS = {
  bg: "#16181d",
  card: "#1e2128",
  border: "#2c313a",
  text: "#e6edf3",
  muted: "#8b949e",
  tool: "#d2a8ff",
  playbook: "#79c0ff",
  user: "#7ee787",
  theo: "#ffa657",
  error: "#ff7b72",
  turn: "#e6edf3",
};

const preSx = {
  m: 0,
  mt: 0.5,
  p: 1,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 11.5,
  lineHeight: 1.45,
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  color: COLORS.text,
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 1,
  maxHeight: 280,
  overflow: "auto",
  ...scrollSx,
};

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mt: 1 }}>
    <Typography sx={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: COLORS.muted }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const Tag: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <Box
    component="span"
    sx={{
      px: 0.75,
      py: "1px",
      borderRadius: 0.75,
      fontSize: 10.5,
      fontWeight: 600,
      color,
      border: `1px solid ${color}55`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

/** summary: a second line under the row, for tools whose output says what they did (plan_and_run, write_api). */
function describeEntry(entry: LiveDebugEntry): { color: string; label: string; title: string; meta: string; summary?: string } {
  if (entry.kind === "turn") {
    const { turn } = entry;
    return {
      color: turn.failed_attempts > 0 ? COLORS.error : COLORS.turn,
      label: `turn ${turn.turn_index}`,
      title: turn.feature || "LLM calls",
      meta: [
        `${turn.attempt_count} API attempts`,
        formatTokens(turn.total_tokens),
        formatCost(turn.total_cost),
      ]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (entry.kind === "progress") {
    return { color: COLORS.muted, label: "progress", title: entry.text, meta: "" };
  }
  if (entry.kind === "tool") {
    const { row } = entry;
    if (row.audit_event_type === "playbook_context") {
      return { color: COLORS.playbook, label: "playbook", title: row.tool_name, meta: "" };
    }
    const failed = isErrorRow(row);
    const running = !row.end_timestamp;
    // plan_and_run and write_api rows say what they did in one line: replans, steps, requests, results, writes.
    const summary = running
      ? null
      : isPlanAndRun(row.tool_name)
        ? planAndRunSummary(row.output_json)
        : isWriteApi(row.tool_name)
          ? writeApiSummary(row.param_json, row.output_json)
          : null;
    return {
      color: failed ? COLORS.error : COLORS.tool,
      label: failed ? "error" : running ? "running" : "",
      title: row.tool_name,
      meta: running ? "" : formatDuration(row.start_timestamp, row.end_timestamp),
      summary: summary || undefined,
    };
  }
  const { message } = entry;
  const isTheo = message?.user_id === "neo";
  const contentType = String(message?.content_type || "TEXT");
  const firstLine = String(message?.content || "").trim().split("\n")[0] || `(${contentType.toLowerCase()})`;
  return {
    color: message?.status === "failed" ? COLORS.error : isTheo ? COLORS.theo : COLORS.user,
    label: [isTheo ? "theo" : "you", contentType !== "TEXT" ? contentType.toLowerCase() : "", message?.isStreaming ? "streaming" : ""]
      .filter(Boolean)
      .join(" · "),
    title: firstLine,
    meta: "",
  };
}

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && value !== "" && !(typeof value === "object" && Object.keys(value).length === 0);

const ToolDetail: React.FC<{ row: AuditRow; view: StructuredView }> = ({ row, view }) => {
  const plan = isPlanAndRun(row.tool_name);
  const write = isWriteApi(row.tool_name);
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
        <Tag color={COLORS.muted}>{row.status || (row.end_timestamp ? "DONE" : "RUNNING")}</Tag>
        {row.audit_event_type !== "playbook_context" && <Tag color={COLORS.muted}>{getActionTypeLabel(row)}</Tag>}
        {row.user_email && <Tag color={COLORS.muted}>{row.user_email}</Tag>}
        {row.audit_turn_id && <Tag color={COLORS.muted}>turn {String(row.audit_turn_id).slice(0, 8)}</Tag>}
        {row.is_undoable && <Tag color={COLORS.user}>undoable</Tag>}
      </Box>
      {row.description && row.description !== row.reasoning && (
        <Typography sx={{ fontSize: 12.5, color: COLORS.text, mt: 0.75 }}>{row.description}</Typography>
      )}
      {row.reasoning && (
        <Section label="Reasoning">
          <Typography sx={{ fontSize: 12.5, color: COLORS.text, whiteSpace: "pre-wrap", mt: 0.25 }}>{row.reasoning}</Typography>
        </Section>
      )}
      {write && (hasValue(row.param_json) || hasValue(row.output_json)) && (
        // write_api: each write with its own input and output, like a request in the browser's network tab
        <Section label="Writes">
          <WriteApiView input={row.param_json} value={row.output_json} view={view} preSx={preSx} />
        </Section>
      )}
      {!write && hasValue(row.param_json) && (
        <Section label="Parameters">
          {plan ? (
            <PlanInput value={row.param_json} view={view} preSx={preSx} />
          ) : (
            <StructuredValue value={row.param_json} view={view} preSx={preSx} />
          )}
        </Section>
      )}
      {row.error_message && (
        <Section label="Error">
          <Box component="pre" sx={{ ...preSx, color: COLORS.error }}>{row.error_message}</Box>
        </Section>
      )}
      {!write && hasValue(row.output_json) && (
        <Section label="Output">
          {plan ? (
            <PlanOutput value={row.output_json} view={view} preSx={preSx} />
          ) : (
            <StructuredValue value={row.output_json} view={view} preSx={preSx} />
          )}
        </Section>
      )}
      {row.call_id && (
        <Typography sx={{ mt: 0.75, fontSize: 10.5, color: COLORS.muted }}>call {row.call_id}</Typography>
      )}
    </>
  );
};

/** A tool row shown over the whole sidebar, with a back arrow to the list. */
const FocusedTool: React.FC<{ entry: Extract<LiveDebugEntry, { kind: "tool" }>; view: StructuredView; onBack: () => void }> = ({
  entry,
  view,
  onBack,
}) => {
  const { color, label, title, meta } = describeEntry(entry);
  return (
    <Box
      role="dialog"
      aria-label={title}
      sx={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", flexDirection: "column", background: COLORS.bg }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, borderBottom: `1px solid ${COLORS.border}` }}>
        <Tooltip title="Back to the list (Esc)">
          <IconButton size="small" onClick={onBack} aria-label="Back" sx={{ color: COLORS.muted }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {label && <Typography sx={{ fontSize: 10.5, color, fontWeight: 600, textTransform: "uppercase" }}>{label}</Typography>}
        <Typography
          sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          noWrap
        >
          {title}
        </Typography>
        {meta && <Typography sx={{ fontSize: 11, color: COLORS.muted }}>{meta}</Typography>}
        <Typography sx={{ fontSize: 11, color: COLORS.muted, whiteSpace: "nowrap" }}>{formatClock(entry.at)}</Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5, pb: 1.5, ...scrollSx }}>
        <ToolDetail row={entry.row} view={view} />
      </Box>
    </Box>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.muted }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{value}</Typography>
  </Box>
);

const MiniStat: React.FC<{ label: string; value: string; hint: string }> = ({ label, value, hint }) => (
  <Tooltip title={hint}>
    <Typography component="span" sx={{ fontSize: 11, color: COLORS.muted, whiteSpace: "nowrap" }}>
      {label}{" "}
      <Box component="span" sx={{ color: COLORS.text, fontWeight: 600 }}>
        {value}
      </Box>
    </Typography>
  </Tooltip>
);

const TurnDetail: React.FC<{ turn: ThreadTurnUsage }> = ({ turn }) => (
  <>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 0.5 }}>
      <Stat label="Input" value={formatTokens(turn.input_tokens)} />
      <Stat label="Output" value={formatTokens(turn.output_tokens)} />
      <Stat label="Cost" value={formatCost(turn.total_cost)} />
      <Stat label="API attempts" value={String(turn.attempt_count)} />
      <Stat label="API time" value={`${(turn.duration_ms / 1000).toFixed(1)}s`} />
      <Stat label="Status" value={turn.failed_attempts > 0 ? "Has failed attempts" : "Success"} />
    </Box>
    <Typography sx={{ mt: 0.75, fontSize: 10.5, color: COLORS.muted }}>
      {turn.feature} · turn {turn.turn_id}
    </Typography>
  </>
);

const MessageDetail: React.FC<{ message: any }> = ({ message }) => {
  const review = message?.human_review;
  const reviewTools = review?.tools?.length
    ? review.tools.map((tool: any) => tool?.tool_name).filter(Boolean)
    : ([] as string[]).concat(review?.tool_name ?? []);
  return (
    <>
      {message?.content ? (
        <Box component="pre" sx={preSx}>{message.content}</Box>
      ) : null}
      {reviewTools.length > 0 && (
        <Section label="Awaiting approval for">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {reviewTools.map((name: string, index: number) => (
              <Tag key={`${name}-${index}`} color={COLORS.tool}>{name}</Tag>
            ))}
          </Box>
        </Section>
      )}
      {message?.error && (
        <Section label="Error">
          <Box component="pre" sx={{ ...preSx, color: COLORS.error }}>{message.error}</Box>
        </Section>
      )}
      <Typography sx={{ mt: 0.75, fontSize: 10.5, color: COLORS.muted }}>
        {[message?.message_id && `id ${message.message_id}`, message?.agent_display_name].filter(Boolean).join(" · ")}
      </Typography>
    </>
  );
};

export interface LiveDebugPanelProps {
  aiThreadId: string | number | null | undefined;
  messages: any[];
  isThinking?: boolean;
  onClose: () => void;
}

const LiveDebugPanel: React.FC<LiveDebugPanelProps> = ({ aiThreadId, messages, isThinking, onClose }) => {
  const threadId = aiThreadId != null && aiThreadId !== "" ? String(aiThreadId) : null;
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [slot, setSlot] = useState<HTMLElement | null>(() => document.getElementById(LIVE_DEBUG_SLOT_ID));
  const requestSeq = useRef(0);
  const requestThread = useRef<string | null>(threadId);
  const [usage, setUsage] = useState<ThreadUsage | null>(null);
  // Root-only routes answer 403/404 for everyone else; stop asking once they have said no.
  const [usageBlocked, setUsageBlocked] = useState(false);
  const [traceUrl, setTraceUrl] = useState<string>("");
  const [automation, setAutomation] = useState<ThreadAutomation | null>(null);
  const [resolver, setResolver] = useState<ThreadResolverState | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate[]>([]);
  const [view, setView] = useState<StructuredView>(readStoredView);
  const seenStepCount = useRef(0);
  const thinkingSteps = useSelector((state: any) => state.chat?.thinkingSteps) as unknown[] | undefined;
  const listRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    if (!slot) setSlot(document.getElementById(LIVE_DEBUG_SLOT_ID));
  }, [slot]);

  const fetchAudit = useCallback(async () => {
    if (!threadId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      chatApi.invalidateThreadAuditCache(threadId);
      const data = await chatApi.getToolAuditLogs(threadId);
      if (seq !== requestSeq.current) return;
      setAuditRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to load audit logs");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [threadId]);

  /** Data that only changes between turns: Usage totals, trace link, mode and resolver state. */
  const fetchTurnData = useCallback(async () => {
    if (!threadId) return;
    const forThread = threadId;
    const [automationResult, resolverResult] = await Promise.allSettled([
      chatApi.getThreadAutomation(forThread),
      chatApi.getThreadResolverState(forThread),
    ]);
    if (requestThread.current !== forThread) return;
    if (automationResult.status === "fulfilled") setAutomation(automationResult.value);
    if (resolverResult.status === "fulfilled") setResolver(resolverResult.value);
    if (usageBlocked) return;
    const [usageResult, traceResult] = await Promise.allSettled([
      getThreadUsage(Number(forThread)),
      chatApi.getLinearTaskTraceUrl(forThread),
    ]);
    if (requestThread.current !== forThread) return;
    const denied = (result: PromiseSettledResult<unknown>) =>
      result.status === "rejected" && [401, 403, 404].includes((result.reason as any)?.response?.status);
    if (denied(usageResult) && denied(traceResult)) {
      setUsageBlocked(true);
      return;
    }
    if (usageResult.status === "fulfilled") setUsage(usageResult.value);
    if (traceResult.status === "fulfilled") setTraceUrl(String(traceResult.value?.trace_url || ""));
  }, [threadId, usageBlocked]);

  // New thread: start clean.
  useEffect(() => {
    requestSeq.current += 1;
    requestThread.current = threadId;
    setAuditRows([]);
    setOverrides({});
    setError(null);
    setUsage(null);
    setTraceUrl("");
    setAutomation(null);
    setResolver(null);
    setProgress([]);
  }, [threadId]);

  // Keep Cortex's progress updates. Redux clears them when the turn ends; the panel keeps a copy.
  useEffect(() => {
    const steps = Array.isArray(thinkingSteps) ? thinkingSteps : [];
    if (steps.length < seenStepCount.current) seenStepCount.current = 0;
    const fresh = steps.slice(seenStepCount.current);
    seenStepCount.current = steps.length;
    if (!threadId || fresh.length === 0) return;
    const now = Date.now();
    setProgress((prev) => {
      const next = [...prev];
      fresh.forEach((step, index) => {
        const text = typeof step === "string" ? step : JSON.stringify(step);
        if (!text.trim() || next[next.length - 1]?.text === text) return;
        next.push({ key: `progress-${now}-${index}`, at: now, text });
      });
      return next;
    });
  }, [thinkingSteps, threadId]);

  // Turn-level data: on open, when a turn ends, and again once LangSmith has caught up.
  useEffect(() => {
    if (!threadId || isThinking) return;
    void fetchTurnData();
    const settle = window.setTimeout(() => void fetchTurnData(), LANGSMITH_SETTLE_MS);
    return () => window.clearTimeout(settle);
  }, [threadId, isThinking, fetchTurnData]);

  // Refetch whenever the conversation moves, and keep polling: fast while Theo works, slow otherwise.
  const messageCount = messages.length;
  useEffect(() => {
    if (!threadId) return;
    void fetchAudit();
    const timer = window.setInterval(() => void fetchAudit(), isThinking ? POLL_WHILE_THINKING_MS : POLL_IDLE_MS);
    return () => window.clearInterval(timer);
  }, [threadId, isThinking, messageCount, fetchAudit]);

  const timeline = useMemo(
    () => buildLiveDebugTimeline(messages, auditRows, usage?.turns ?? [], progress),
    [messages, auditRows, usage, progress]
  );
  // Only messages and tool calls auto-open; turn markers and progress lines stay folded.
  const latestKey = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i -= 1) {
      if (timeline[i].kind === "message" || timeline[i].kind === "tool") return timeline[i].key;
    }
    return undefined;
  }, [timeline]);

  // Follow new entries like a chat, unless the user scrolled up to read something.
  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
  }, [timeline]);
  const isOpen = (key: string) => overrides[key] ?? key === latestKey;
  const toggle = (key: string) => setOverrides((prev) => ({ ...prev, [key]: !isOpen(key) }));

  // A plan opens over the whole sidebar instead of expanding in the list. The list stays mounted
  // underneath so its scroll position is where the user left it.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const focused = useMemo(() => {
    const entry = focusedKey ? timeline.find((e) => e.key === focusedKey) : undefined;
    return entry && entry.kind === "tool" ? entry : null;
  }, [focusedKey, timeline]);
  useEffect(() => {
    if (!focused) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFocusedKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focused]);
  useEffect(() => setFocusedKey(null), [threadId]);
  // plan_and_run and write_api open over the whole sidebar; their request/response pairs need the room.
  const isPlanEntry = (entry: LiveDebugEntry) =>
    entry.kind === "tool" && (isPlanAndRun(entry.row.tool_name) || isWriteApi(entry.row.tool_name));
  const activate = (entry: LiveDebugEntry) => (isPlanEntry(entry) ? setFocusedKey(entry.key) : toggle(entry.key));

  const toolCount = auditRows.length;
  const counts = useMemo(() => debugCounts(messages, auditRows), [messages, auditRows]);
  const errorCount = auditRows.filter((row) => isErrorRow(row)).length;

  const panel = (
    <Box
      className="theo-live-debug-panel"
      sx={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: COLORS.bg,
        color: COLORS.text,
        borderLeft: `1px solid ${COLORS.border}`,
        fontFamily: "var(--theo-font-sans)",
        position: slot ? "relative" : undefined,
        ...(slot
          ? {}
          : { position: "fixed", top: 0, right: 0, bottom: 0, height: "100vh", zIndex: 1300, boxShadow: "-8px 0 24px rgba(0,0,0,0.25)" }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${COLORS.border}` }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: isThinking ? COLORS.user : COLORS.muted,
            boxShadow: isThinking ? `0 0 6px ${COLORS.user}` : "none",
          }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>Live debug</Typography>
        <Typography sx={{ fontSize: 11.5, color: COLORS.muted, flex: 1, minWidth: 0 }} noWrap>
          {threadId ? `thread ${threadId} · ${toolCount} tool${toolCount === 1 ? "" : "s"}` : "no thread"}
          {errorCount > 0 ? ` · ${errorCount} error${errorCount === 1 ? "" : "s"}` : ""}
        </Typography>
        <Tooltip title="How tool parameters and output are shown">
          <Box sx={{ display: "flex", border: `1px solid ${COLORS.border}`, borderRadius: 0.75, overflow: "hidden" }}>
            {STRUCTURED_VIEWS.map((option) => (
              <Box
                key={option}
                component="button"
                type="button"
                onClick={() => {
                  setView(option);
                  storeView(option);
                }}
                sx={{
                  px: 0.75,
                  py: "1px",
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  border: 0,
                  cursor: "pointer",
                  color: view === option ? COLORS.text : COLORS.muted,
                  background: view === option ? COLORS.border : "transparent",
                }}
              >
                {option}
              </Box>
            ))}
          </Box>
        </Tooltip>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                void fetchAudit();
                void fetchTurnData();
              }}
              disabled={!threadId || loading}
              sx={{ color: COLORS.muted }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        {traceUrl && (
          <Tooltip title="Open latest turn in LangSmith">
            <IconButton size="small" component="a" href={traceUrl} target="_blank" rel="noopener noreferrer" sx={{ color: COLORS.muted }}>
              <OpenInNewIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Turn off live debug">
          <IconButton size="small" onClick={onClose} sx={{ color: COLORS.muted }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {(usage || automation || resolver?.resolver_state) && (
        <Box sx={{ px: 1.5, py: 0.5, borderBottom: `1px solid ${COLORS.border}` }}>
          {usage && (
            <Box data-testid="live-debug-stats" sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.25, rowGap: 0.25 }}>
              <MiniStat label="in" value={formatTokens(usage.input_tokens)} hint="Tokens in" />
              <MiniStat label="cached" value={formatCached(usage.input_cached_tokens, usage.input_tokens)} hint="Cached input tokens" />
              <MiniStat label="out" value={formatTokens(usage.output_tokens)} hint="Tokens out" />
              <MiniStat label="cost" value={formatCost(usage.total_cost)} hint="Estimated OpenAI cost" />
              <MiniStat label="model" value={`${(usage.turns.reduce((n, t) => n + (t.duration_ms || 0), 0) / 1000).toFixed(1)}s`} hint="Model time" />
              <MiniStat label="turns·calls" value={`${usage.turns.length}·${usage.attempt_count}`} hint="Turns · model calls" />
              <MiniStat
                label="API"
                value={counts.apiFailed ? `${counts.apiCalls} · ${counts.apiFailed} failed` : String(counts.apiCalls)}
                hint="Platform API calls: steps plan_and_run ran plus writes sent"
              />
              <MiniStat
                label="writes"
                value={counts.writeEndpoints && counts.writeEndpoints !== counts.writeCalls ? `${counts.writeCalls} (${counts.writeEndpoints} distinct)` : String(counts.writeCalls)}
                hint="Writes sent to the platform"
              />
              <MiniStat label="plan&run·replans" value={`${counts.planAndRuns}·${counts.replans}`} hint="plan_and_run calls · replans inside them" />
              <MiniStat label="HITL" value={String(counts.hitlCards)} hint="Approval and question cards" />
            </Box>
          )}
          {(automation || resolver?.resolver_state) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: usage ? 1 : 0 }}>
              {automation && (
                <>
                  <Tag color={COLORS.theo}>{AUTOMATION_LABEL[automation.automation_capacity] ?? `mode ${automation.automation_capacity}`}</Tag>
                  {automation.automation_capacity !== 0 && (
                    <Tag color={COLORS.muted}>{INTERACTION_LABEL[automation.customer_interaction] ?? `interaction ${automation.customer_interaction}`}</Tag>
                  )}
                  {automation.agent_id ? <Tag color={COLORS.muted}>agent {automation.agent_id}</Tag> : null}
                </>
              )}
              {resolver?.resolver_state && (
                <Tag color={COLORS.tool}>
                  resolver {resolver.resolver_state}
                  {resolver.resolver_context?.on ? ` · on ${resolver.resolver_context.on}` : ""}
                  {resolver.resolver_context?.kind ? ` · ${resolver.resolver_context.kind}` : ""}
                </Tag>
              )}
            </Box>
          )}
          {resolver?.resolver_context?.reason && (
            <Typography sx={{ mt: 0.5, fontSize: 11.5, color: COLORS.muted }}>{resolver.resolver_context.reason}</Typography>
          )}
        </Box>
      )}
      {error && (
        <Typography sx={{ px: 1.5, py: 0.75, fontSize: 12, color: COLORS.error, borderBottom: `1px solid ${COLORS.border}` }}>
          {error}
        </Typography>
      )}
      <Box
        ref={listRef}
        onScroll={(event) => {
          const list = event.currentTarget;
          stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 48;
        }}
        sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1, display: "flex", flexDirection: "column", gap: 0.75, ...scrollSx }}>
        {timeline.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: COLORS.muted, p: 1 }}>
            {threadId ? "Nothing yet. Messages and tool calls show up here as they happen." : "Open a chat to start."}
          </Typography>
        )}
        {timeline.map((entry) => {
          if (entry.kind === "progress") {
            return (
              <Box key={entry.key} sx={{ display: "flex", gap: 1, px: 1, flexShrink: 0, alignItems: "baseline" }}>
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted, flex: 1, minWidth: 0, fontStyle: "italic" }} noWrap title={entry.text}>
                  › {entry.text}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: COLORS.muted, whiteSpace: "nowrap" }}>{formatClock(entry.at)}</Typography>
              </Box>
            );
          }
          const open = !isPlanEntry(entry) && isOpen(entry.key);
          const { color, label, title, meta, summary } = describeEntry(entry);
          return (
            <Box
              key={entry.key}
              sx={{
                background: COLORS.card,
                border: `1px solid ${open ? `${color}66` : COLORS.border}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 1,
                flexShrink: 0,
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => activate(entry)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    activate(entry);
                  }
                }}
                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, cursor: "pointer", userSelect: "none" }}
              >
                {label && (
                  <Typography sx={{ fontSize: 10.5, color, fontWeight: 600, whiteSpace: "nowrap", textTransform: "uppercase" }}>
                    {label}
                  </Typography>
                )}
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: entry.kind === "tool" ? 13 : 12.5,
                    fontWeight: entry.kind === "tool" ? 600 : 400,
                    color: entry.kind === "tool" ? color : COLORS.text,
                    fontFamily: entry.kind === "tool" ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
                  }}
                  noWrap
                >
                  {title}
                </Typography>
                {meta && <Typography sx={{ fontSize: 11, color: COLORS.muted }}>{meta}</Typography>}
                <Typography sx={{ fontSize: 11, color: COLORS.muted, whiteSpace: "nowrap" }}>{formatClock(entry.at)}</Typography>
              </Box>
              {summary && (
                <Typography
                  data-testid="live-debug-row-summary"
                  sx={{ px: 1, pb: 0.75, mt: -0.5, fontSize: 11, color: COLORS.muted, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                  noWrap
                  title={summary}
                >
                  {summary}
                </Typography>
              )}
              {open && (
                <Box sx={{ px: 1, pb: 1 }}>
                  {entry.kind === "tool" ? (
                    <ToolDetail row={entry.row} view={view} />
                  ) : entry.kind === "turn" ? (
                    <TurnDetail turn={entry.turn} />
                  ) : (
                    <MessageDetail message={entry.message} />
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {focused && <FocusedTool entry={focused} view={view} onBack={() => setFocusedKey(null)} />}
    </Box>
  );

  return createPortal(panel, slot ?? document.body);
};

export default LiveDebugPanel;
